import {
  LinearProgress,
  Stack,
  Box,
  Typography,
  useTheme,
  Pagination,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  gridPageCountSelector,
  gridPageSelector,
  GridRenderCellParams,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import useStyles from "./styles";
import { ptBR } from "@mui/x-data-grid/locales";
import { MyBarHorizontalDashConfig } from "./types";
import handleColors from "../utils/handleColors";
import TooltipMui from "../../tooltipMui";
import { getColorsArray } from "../../../utils/getColorsPallet";

const BarHorizontal: React.FC<MyBarHorizontalDashConfig> = (props) => {
  const { data, indexBy, keys, colorsByKey, colors } = props;

  // const formattedData = formatSeries(data, indexBy, keys);
  const apiRefAloc = useGridApiRef();
  const dataGridRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const styles = useStyles(theme);
  const [loadingAloc, setLoadingAloc] = useState<boolean>(false);
  const [rowHeight, setRowHeight] = useState<number>(70);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const rowsSorted = data?.sort((a, b) => {
    if (!keys || keys.length === 0) {
      return 0;
    }

    const valueA = Number(a[keys[0]]);
    const valueB = Number(b[keys[0]]);
    return valueB - valueA; // Ordena em ordem decrescente
  });

  let biggerValue = 0;
  const rows = rowsSorted?.map((item, index) => {
    const value = keys && keys.length > 0 ? Number(item[keys[0]]) : 0;

    if (Number(value) > biggerValue) {
      biggerValue = Number(value);
    }
    return {
      id: index + 1,
      ...item,
    };
  });
  const numItems = rows?.length ?? 0;

  const progressColors =
    (colors && getColorsArray(numItems, colors, false)) || [];

  // Define os tipos das colunas
  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", width: 90 },
    {
      field: indexBy,
      headerName: indexBy,
      width: 180,
      display: "flex",
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TooltipMui valueX={params.value}>
            <Box sx={styles.text}>{params.value}</Box>
          </TooltipMui>
        );
      },
    },
    ...((keys || []).map((key) => ({
      field: key,
      headerName: key,
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const percent = params.value ? Math.min(params.value) : 0;
        const formattedValue = params.row?.[indexBy]?.toString();

        return (
          <TooltipMui
            color={
              props?.colorsByKey
                ? handleColors(key, theme, props.colorsByKey)
                : String(progressColors?.[params.row.id - 1] ?? "#13C06A")
            }
            valueX={formattedValue + ": " + params.value}
          >
            <Box key={params.row.id} sx={styles.boxLinearProgress} gap={1}>
              <LinearProgress
                sx={[
                  styles.linearProgress,
                  {
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: colorsByKey
                        ? handleColors(key, theme, colorsByKey)
                        : colors && progressColors
                          ? progressColors[params.row.id - 1]
                          : undefined,
                    },
                    backgroundColor: theme.palette.background.textField,
                  },
                ]}
                variant="determinate"
                value={(percent / biggerValue) * 100}
              />
              <Typography sx={styles.textProgress}>{percent}</Typography>
            </Box>
          </TooltipMui>
        );
      },
    })) as GridColDef[]),
  ];

  function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        count={Number(pageCount)}
        page={Number(page) + 1}
        onChange={(event, value) => apiRef.current.setPage(value - 1)}
      />
    );
  }
  useEffect(() => {
    if (dataGridRef.current) {
      let { offsetHeight } = dataGridRef.current;
      offsetHeight = offsetHeight;
      let newRowHeight = 55;
      const newPageSize = Math.floor(offsetHeight / newRowHeight);
      setPaginationModel({
        pageSize: newPageSize,
        page: 0,
      });
    }
  }, [dataGridRef.current]);

  return (
    <DataGrid
      ref={dataGridRef}
      key={"barHorizontal" + numItems || ""}
      apiRef={apiRefAloc}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      loading={loadingAloc}
      density="compact"
      // autoPageSize
      sx={styles.dataGrid}
      slotProps={{
        loadingOverlay: {
          variant: "skeleton",
          noRowsVariant: "skeleton",
        },
        // columnHeaders: { style: { display: "none" } },
      }}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 5,
          },
        },
      }}
      // columnHeaderHeight={0}
      rowHeight={rowHeight}
      // hideFooter
      columnVisibilityModel={{ id: false }}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
      }
      // autoHeight
      disableColumnFilter={true}
      disableColumnSorting={true}
      disableColumnMenu={true}
      rows={rows}
      columns={columns}
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      disableRowSelectionOnClick
      slots={{
        pagination: CustomPagination,
      }}
    />
  );
};

export default BarHorizontal;
